import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { ObjectHierarchicalPicker } from './ObjectHierarchicalPicker';
import { HIERARCHY_ROOT_IDS, ObjectMetadataValue } from '../types/ObjectHierarchicalMetadataValue';
import {
  bulkEditObjects,
  BulkEditFields,
  BulkEditListMode,
  BulkEditResponse,
  BulkProvenanceRecord,
} from '../utils/bulkEditApi';
import { logger } from '../utils/logger';

/** Tri-state for the bulk "Marked to be Sold" control. */
type MarkedAsSoldMode = 'unchanged' | 'sold' | 'notSold';

/**
 * Editor-only draft of a provenance record. The stable `key` is a client-side
 * React list key (NOT sent to the API — the wire model forbids unknown fields);
 * it survives mid-list removal so input focus/IME state stays on the right row.
 */
interface ProvenanceDraft extends BulkProvenanceRecord {
  key: number;
}

interface BulkEditDialogProps {
  open: boolean;
  /** Catalog item IDs the edit will be applied to. */
  selectedIds: string[];
  /** Delegator user id when editing a delegated catalog; null/undefined for own. */
  ownerId?: string | null;
  onClose: () => void;
  /** Called after a successful apply with the updated-vs-failed summary. */
  onApplied: (summary: BulkEditResponse) => void;
}

const asArray = (value: ObjectMetadataValue | ObjectMetadataValue[]): ObjectMetadataValue[] =>
  Array.isArray(value) ? value : [value];

/**
 * Bulk Edit dialog — applies a subset of shareable metadata to every selected
 * object. Getty inputs reuse {@link ObjectHierarchicalPicker} so owner-scoped
 * Getty favorites (under delegation, #952) work unchanged; the picker reads the
 * active catalog owner from context via GettyFavoritesContext. Only the fields
 * the user fills are sent; list fields honor the add-vs-replace choice.
 */
export const BulkEditDialog = ({ open, selectedIds, ownerId, onClose, onApplied }: BulkEditDialogProps) => {
  const [creationYear, setCreationYear] = useState('');
  const [workType, setWorkType] = useState<ObjectMetadataValue | null>(null);
  const [materials, setMaterials] = useState<ObjectMetadataValue[]>([]);
  const [techniques, setTechniques] = useState<ObjectMetadataValue[]>([]);
  const [substrate, setSubstrate] = useState<ObjectMetadataValue[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [markedAsSoldMode, setMarkedAsSoldMode] = useState<MarkedAsSoldMode>('unchanged');
  const [provenanceEntries, setProvenanceEntries] = useState<ProvenanceDraft[]>([]);
  // Monotonic source of stable list keys for provenance drafts.
  const provenanceKeySeq = useRef(0);
  const [listMode, setListMode] = useState<BulkEditListMode>('add');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the dialog (re)opens so a prior edit doesn't bleed
  // into the next selection.
  useEffect(() => {
    if (open) {
      setCreationYear('');
      setWorkType(null);
      setMaterials([]);
      setTechniques([]);
      setSubstrate([]);
      setKeywords([]);
      setMarkedAsSoldMode('unchanged');
      setProvenanceEntries([]);
      setListMode('add');
      setApplying(false);
      setError(null);
    }
  }, [open]);

  // Provenance entries are edited immutably (never mutate state in place).
  const addProvenanceEntry = () =>
    setProvenanceEntries((prev) => [
      ...prev,
      { key: (provenanceKeySeq.current += 1), publicRendering: '', internalRendering: '', isPublic: false },
    ]);
  const updateProvenanceEntry = (key: number, patch: Partial<BulkProvenanceRecord>) =>
    setProvenanceEntries((prev) => prev.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)));
  const removeProvenanceEntry = (key: number) =>
    setProvenanceEntries((prev) => prev.filter((entry) => entry.key !== key));

  // Only entries that carry some text are worth appending; fully-blank rows are
  // ignored. A "public" entry with no public rendering would never show on the
  // public route, so it is treated as invalid (blocks Apply) rather than sent.
  const provenanceToSend = provenanceEntries.filter(
    (entry) => entry.publicRendering.trim() !== '' || entry.internalRendering.trim() !== '',
  );
  const hasInvalidPublicEntry = provenanceToSend.some(
    (entry) => entry.isPublic && entry.publicRendering.trim() === '',
  );

  const buildFields = (): BulkEditFields => {
    const fields: BulkEditFields = {};
    if (creationYear.trim()) fields.creationYear = creationYear.trim();
    if (workType) fields.workType = workType;
    if (materials.length > 0) fields.materials = materials;
    if (techniques.length > 0) fields.techniques = techniques;
    if (substrate.length > 0) fields.substrate = substrate;
    if (keywords.length > 0) fields.keywords = keywords;
    if (markedAsSoldMode === 'sold') fields.markedAsSold = true;
    else if (markedAsSoldMode === 'notSold') fields.markedAsSold = false;
    if (provenanceToSend.length > 0) {
      // Strip the editor-only `key`; the API model forbids unknown fields.
      fields.provenance = provenanceToSend.map((entry) => ({
        publicRendering: entry.publicRendering,
        internalRendering: entry.internalRendering,
        isPublic: entry.isPublic,
      }));
    }
    return fields;
  };

  const hasAnyField =
    creationYear.trim().length > 0 ||
    workType !== null ||
    materials.length > 0 ||
    techniques.length > 0 ||
    substrate.length > 0 ||
    keywords.length > 0 ||
    markedAsSoldMode !== 'unchanged' ||
    provenanceToSend.length > 0;

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const summary = await bulkEditObjects(selectedIds, buildFields(), listMode, ownerId);
      onApplied(summary);
    } catch (err) {
      logger.error('[BulkEditDialog] apply failed', err);
      setError('Failed to apply bulk edit. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Edit {selectedIds.length} object{selectedIds.length === 1 ? '' : 's'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only the fields you fill in below are applied. Untouched fields are left unchanged on each
          object. Title, catalog number, and creators are unique per object and can&apos;t be bulk edited.
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Creation year"
            value={creationYear}
            onChange={(e) => setCreationYear(e.target.value)}
            placeholder="e.g. 1972"
            fullWidth
          />

          <ObjectHierarchicalPicker
            hierarchyRootId={HIERARCHY_ROOT_IDS.workTypes}
            label="Work Type"
            multiple={false}
            chipPrefix="AAT"
            value={workType ?? []}
            onChange={(v) => {
              const arr = asArray(v);
              setWorkType(arr.length > 0 ? arr[arr.length - 1] : null);
            }}
          />

          <ObjectHierarchicalPicker
            hierarchyRootId={HIERARCHY_ROOT_IDS.techniques}
            label="Techniques"
            multiple
            chipPrefix="AAT"
            value={techniques}
            onChange={(v) => setTechniques(asArray(v))}
          />

          <ObjectHierarchicalPicker
            hierarchyRootId={HIERARCHY_ROOT_IDS.materials}
            label="Materials"
            multiple
            chipPrefix="AAT"
            value={materials}
            onChange={(v) => setMaterials(asArray(v))}
          />

          <ObjectHierarchicalPicker
            hierarchyRootId={HIERARCHY_ROOT_IDS.substrates}
            label="Substrate"
            multiple
            chipPrefix="AAT"
            value={substrate}
            onChange={(v) => setSubstrate(asArray(v))}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={keywords}
            onChange={(_event, value) => setKeywords(value as string[])}
            renderInput={(params) => (
              <TextField {...params} label="Keywords" placeholder="Type and press Enter" />
            )}
          />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              For list fields (techniques, materials, substrate, keywords):
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={listMode}
              onChange={(_event, value) => {
                if (value) setListMode(value as BulkEditListMode);
              }}
              aria-label="List field apply mode"
            >
              <ToggleButton value="add" aria-label="Add to existing">
                Add to existing
              </ToggleButton>
              <ToggleButton value="replace" aria-label="Replace existing">
                Replace existing
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Marked to be Sold
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={markedAsSoldMode}
              onChange={(_event, value) => {
                if (value) setMarkedAsSoldMode(value as MarkedAsSoldMode);
              }}
              aria-label="Marked to be sold"
            >
              <ToggleButton value="unchanged">Leave unchanged</ToggleButton>
              <ToggleButton value="sold">Mark as sold</ToggleButton>
              <ToggleButton value="notSold">Mark as not sold</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Provenance
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Records are always <strong>appended</strong> to each object (existing history is preserved)
              and are not affected by the add/replace toggle above.
            </Typography>

            <Stack spacing={2}>
              {provenanceEntries.map((entry) => (
                <Box key={entry.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <TextField
                    label="Public rendering"
                    value={entry.publicRendering}
                    onChange={(e) => updateProvenanceEntry(entry.key, { publicRendering: e.target.value })}
                    placeholder="Shown publicly when the record is public"
                    multiline
                    minRows={2}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="Internal rendering"
                    value={entry.internalRendering}
                    onChange={(e) => updateProvenanceEntry(entry.key, { internalRendering: e.target.value })}
                    placeholder="Internal-only notes"
                    multiline
                    minRows={2}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={entry.isPublic}
                          onChange={(e) => updateProvenanceEntry(entry.key, { isPublic: e.target.checked })}
                        />
                      }
                      label="Is public"
                    />
                    <Button size="small" color="error" onClick={() => removeProvenanceEntry(entry.key)}>
                      Remove
                    </Button>
                  </Box>
                  {entry.isPublic && entry.publicRendering.trim() === '' && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      A public record needs a public rendering, or it will never be shown publicly.
                    </Typography>
                  )}
                </Box>
              ))}
              <Button size="small" variant="outlined" onClick={addProvenanceEntry} sx={{ alignSelf: 'flex-start' }}>
                Add provenance entry
              </Button>
            </Stack>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!hasAnyField || applying || hasInvalidPublicEntry}
          startIcon={applying ? <CircularProgress size={16} /> : undefined}
        >
          Apply to {selectedIds.length}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
